import React, { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Banner, IconStatusUrgent, Input } from '@seismic/mantle';
import SeismicPlayer from '@seismic/universal-player';
import { v4 as uuidv4 } from 'uuid';
import { IRecordingState, RecordingStateActions, UploadStatus } from '../../models';
import { IRecordingStateReducerAction } from '../../hooks';
import styles from './Preview.scss';
import { useTranslation } from 'react-i18next';
import UCP from '@seismic/universal-content-picker';
import { GatewayContext } from '../../context';

const UVR_APPLICATION_NAME = 'Universal Video Recorder';

interface PreviewProps {
  state: IRecordingState;
  dispatch: React.Dispatch<IRecordingStateReducerAction>;
}

export default function Preview(props: PreviewProps): ReactElement {
  const { t } = useTranslation();
  const { state, dispatch } = props;
  const { fileName } = state;
  const video = state.recorder.blob;
  const fileNameRef = useRef<any>();
  const [didUserChangeName, setDidUserChangeName] = useState<boolean>(false);
  const [fileNameError, setFileNameError] = useState<string>('');
  const { getAuthToken } = useContext(GatewayContext);

  // useCallback would cause UP to rerender - not sure why
  // so we memoize the manifest instead
  const fakeManifest = useMemo((): any => {
    if (!video) {
      return {};
    }

    return {
      ContentManifestId: uuidv4(),
      AllowDownload: true,
      AllowPrint: true,
      Content: {
        TeamsiteId: '',
        Id: uuidv4(),
        VersionId: uuidv4(),
        Repository: 'library',
        Type: 'file'
      },
      CoverImageUrl: '',
      Manifests: [
        {
          FormatType: 'Video',
          NativeFormat: 'webm',
          Url: `${URL.createObjectURL(video)}#t=0.001`, // Temporary until UP fixes the poster image issue
          DownloadUrl: URL.createObjectURL(video),
          Mode: 'read',
          Title: 'Mp4',
          ViewerDetail: {},
          Permissions: {
            AllowDownload: false,
            AllowPrint: false
          },
          Error: null,
          ErrorReason: 'None',
          DowngradeReason: null,
          IsCacheable: true
        }
      ],
      Modes: ['read'],
      LogUrl: ''
    };
  }, [video]);

  useEffect(() => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    const fileName = `New Recording - ${formatter.format(now)}`;

    dispatch({ type: RecordingStateActions.SetFileName, payload: fileName });
  }, [dispatch]);

  useEffect(() => {
    // need to check for fileName because it is undefined
    // until the fileName is set from the current date
    if (fileNameRef && fileNameRef.current && fileName && !didUserChangeName) {
      fileNameRef.current.select();
    }
  }, [fileName, didUserChangeName]);

  const onFolderChange = (folder) => {
    if (folder === undefined) {
      dispatch({ type: RecordingStateActions.SetTargetFolder, payload: undefined });
      return;
    }

    dispatch({
      type: RecordingStateActions.SetTargetFolder,
      payload: {
        ...folder,
        teamsiteId: folder.teamSiteId === undefined ? null : folder.teamSiteId
      }
    });
  };

  const getInvalidCharacterTranslation = (invalidCharacter: string): string => {
    return t('Preview.FileName.Error.InvalidCharacter', {
      defaultValue: `File name cannot start with "${invalidCharacter}"`,
      replace: { invalidCharacter }
    });
  };

  const checkFileNameValidity = (fileName: string) => {
    switch (fileName.charAt(0)) {
      case '': {
        setFileNameError(t('Preview.FileName.Error.Empty', { defaultValue: 'File name cannot be empty' }));
        return false;
      }
      case '+': {
        setFileNameError(getInvalidCharacterTranslation('+'));
        return false;
      }
      case '-': {
        setFileNameError(getInvalidCharacterTranslation('-'));
        return false;
      }
      case '=': {
        setFileNameError(getInvalidCharacterTranslation('='));
        return false;
      }
      default: {
        return true;
      }
    }
  };

  function onFileNameChange(e): void {
    setDidUserChangeName(true);
    const isValid = checkFileNameValidity(e.target.value);
    if (isValid) {
      dispatch({ type: RecordingStateActions.SetFileName, payload: e.target.value });
    } else {
      dispatch({ type: RecordingStateActions.SetFileName, payload: undefined });
    }
  }

  return (
    <div className={styles.previewContainer} data-testid="previewContainer">
      {state.uploadStatus === UploadStatus.Failure && (
        <div className={styles.uploadBanner} data-testid="errorBanner">
          <Banner
            variant="error"
            message={t('Preview.Error.Banner', { defaultValue: 'Your recording failed to save' })}
          />
        </div>
      )}
      <div className={styles.videoPreviewBackground}>
        {video && (
          <div className={styles.videoPreviewContainer} data-testid="videoPreviewContainer">
            <SeismicPlayer config={fakeManifest} verifySignature={false} />
          </div>
        )}
      </div>
      <div className={styles.videoAttributes}>
        <label className={styles.saveAsLabel} htmlFor="saveAs">
          {t('Preview.SaveAs', { defaultValue: 'Save as' })}
        </label>
        <Input
          id="fileName"
          data-testid="fileName"
          className={`${styles.fileName} ${
            // need to check for fileNameError
            // otherwise, on load, there will be a flash of the error as the file name is checked
            state.fileName === undefined && fileNameError ? styles.withError : ''
          }`}
          ref={fileNameRef}
          placeholder={t('Preview.FileName.Placeholder', { defaultValue: 'Enter file name...' })}
          value={state.fileName}
          onChange={onFileNameChange}
        />
        {state.fileName === undefined && (
          <div data-testid="fileNameErrorMessage" className={styles.fileNameErrorMessage}>
            <IconStatusUrgent size={16} className={styles.fileNameErrorMessageIcon} />
            {fileNameError}
          </div>
        )}
        <label className={styles.fileLocationLabel} htmlFor="fileLocation">
          {t('Preview.FileLocation', { defaultValue: 'File location' })}
        </label>
        <div className={styles.fileLocation} data-testid="fileLocation">
          <UCP
            apiOptions={{
              authTokenResolver: getAuthToken
            }}
            settings={{
              application: UVR_APPLICATION_NAME,
              repositories: ['workspace'],
              viewMode: 'narrow',
              assetTypeFilter: {
                filterType: 'whitelist',
                values: ['Folder']
              },
              selectionMode: 'single'
            }}
            callbacks={{
              onFolderChange
            }}
          />
        </div>
      </div>
    </div>
  );
}
